import { useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import "@/App.css";
import SalesPage from "@/pages/SalesPage";
import EigentlichPage from "@/pages/EigentlichPage";
import EigentlichDanke from "@/pages/EigentlichDanke";
import EigentlichFastGeschafft from "@/pages/EigentlichFastGeschafft";
import StatsPage from "@/pages/StatsPage";
import { initMetaPixel, trackMetaEvent } from "@/utils/metaPixel";
import usePageMeta from "@/hooks/usePageMeta";

const DATENSCHUTZ_URL = "https://luisabergholz.com/datenschutzerklaerung";
const CONSENT_KEY = "mc_cookie_consent";
const STATS_PATH = "/masterclass-statistiken";

const ease = [0.22, 1, 0.36, 1];

const PageMetaApplier = () => {
  usePageMeta();
  return null;
};

const readConsent = () => {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
};

const CookieBanner = ({ onAccept, onDecline }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease }}
    className="fixed inset-x-0 bottom-0 z-[90] p-4 sm:p-6"
    data-testid="cookie-banner"
  >
    <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-lg border border-[#EBC479]/60 bg-[#FFFBEE] p-5 shadow-[0_24px_70px_-20px_rgba(42,15,8,0.5)] sm:flex-row sm:items-center sm:gap-6 sm:p-6">
      <p className="flex-1 text-sm leading-relaxed text-[#5C1D10]/85">
        Um Deine Erfahrung auf unserer Website optimal zu gestalten und fortlaufend verbessern zu können, verwenden wir Cookies und ähnliche Technologien wie den Meta-Pixel. Manche davon sind technisch notwendig, andere helfen uns, das Angebot zu verbessern und Dir relevante Inhalte zu zeigen. Mehr dazu in unserer{" "}
        <a href={DATENSCHUTZ_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-[#5C1D10]">Datenschutzerklärung</a>.
      </p>
      <div className="flex shrink-0 gap-3">
        <button type="button" onClick={onDecline} data-testid="cookie-decline"
          className="rounded-sm border border-[#B26B3F]/50 px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-[#5C1D10] transition-colors hover:bg-[#B26B3F]/10">
          Nur notwendige
        </button>
        <button type="button" onClick={onAccept} data-testid="cookie-accept"
          className="rounded-sm bg-[#5C1D10] px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-wider text-[#FFF2B0] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_40px_-14px_rgba(92,29,16,0.7)]">
          Alle akzeptieren
        </button>
      </div>
    </div>
  </motion.div>
);

/* Loads the Meta Pixel only after explicit consent and tracks SPA page views.
   The private stats page is excluded (no banner, no tracking). */
function CookieConsentGate() {
  const location = useLocation();
  const [consent, setConsent] = useState(readConsent);
  const excluded = location.pathname.startsWith(STATS_PATH);

  useEffect(() => {
    if (excluded || consent !== "accepted") return;
    initMetaPixel();
    trackMetaEvent("PageView");
  }, [consent, location.key, excluded]);

  const handleConsent = (value) => {
    try {
      localStorage.setItem(CONSENT_KEY, value);
    } catch { /* storage unavailable */ }
    setConsent(value);
  };

  if (excluded || consent !== null) return null;
  return <CookieBanner onAccept={() => handleConsent("accepted")} onDecline={() => handleConsent("declined")} />;
}

function App() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App noise">
      <BrowserRouter>
        <CookieConsentGate />
        <PageMetaApplier />
        <Routes>
          <Route path="/" element={<SalesPage />} />
          {/* Ads-Variante: identische Komponente - Änderungen gelten automatisch für beide URLs */}
          <Route path="/soytfb1126" element={<SalesPage />} />
          <Route path="/eigentlich" element={<EigentlichPage />} />
          <Route path="/eigentlich/danke" element={<EigentlichDanke />} />
          <Route path="/eigentlich/fast-geschafft" element={<EigentlichFastGeschafft />} />
          <Route
            path="/eigentlich-27f819b"
            element={
              <EigentlichPage
                source="eigentlich-masterclass-fbads"
                showDateTime={false}
              />
            }
          />
          <Route path="/eigentlich-27f819b/danke" element={<EigentlichDanke />} />
          <Route path={STATS_PATH} element={<StatsPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
